import React, { useCallback, useRef } from "react";
import { CheckCircle2, Truck } from "lucide-react";
import { nextDueDate, shortDueLabel } from "../../../data/taxPeriods.js";

const BASE_TRANSFORM = "perspective(1200px) rotateY(-6deg) rotateX(3deg)";

// Decorative, non-interactive preview of the product (sample data only) -
// gives the hero a "real app" feel without needing a photo/illustration
// asset. Recolored to the landing page's own blue/indigo/emerald palette.
//
// The card and its two floating badges tilt/parallax toward the cursor
// (desktop mouse-move only - touch never fires mousemove, so it just sits at
// its base tilt there) for a "real 3D object" feel. Driven via direct DOM
// writes on refs rather than React state, so it costs no re-renders per
// mouse move. The badges use the standalone CSS `translate` property (not
// `transform`) for the parallax offset so it composes with their existing
// animate-float/-slow keyframes instead of fighting them for the same
// `transform` property.
const HeroMockup = () => {
  const nextDue = nextDueDate();
  const wrapRef = useRef(null);
  const cardRef = useRef(null);
  const glowRef = useRef(null);
  const badgeRefs = useRef([]);
  const frameRef = useRef(null);

  const handleMove = useCallback((e) => {
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;

    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => {
      const rotateY = -6 + (px - 0.5) * 22;
      const rotateX = 3 + (0.5 - py) * 16;
      if (cardRef.current) {
        cardRef.current.style.transform = `perspective(1200px) rotateY(${rotateY.toFixed(2)}deg) rotateX(${rotateX.toFixed(2)}deg) translateZ(10px)`;
      }
      if (glowRef.current) {
        glowRef.current.style.background = `radial-gradient(circle at ${(px * 100).toFixed(1)}% ${(py * 100).toFixed(1)}%, rgba(255,255,255,.35), transparent 55%)`;
      }
      badgeRefs.current.forEach((el, i) => {
        if (!el) return;
        const depth = i === 0 ? 22 : -18;
        const dx = (px - 0.5) * depth;
        const dy = (py - 0.5) * depth;
        el.style.translate = `${dx.toFixed(1)}px ${dy.toFixed(1)}px`;
      });
    });
  }, []);

  const handleLeave = useCallback(() => {
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    if (cardRef.current) cardRef.current.style.transform = BASE_TRANSFORM;
    if (glowRef.current) glowRef.current.style.background = "transparent";
    badgeRefs.current.forEach((el) => {
      if (el) el.style.translate = "";
    });
  }, []);

  return (
    <div
      ref={wrapRef}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="relative [perspective:1400px]"
    >
      {/* Soft aurora glow behind the card for depth, matching the hero's own mesh background */}
      <div className="landing-aurora absolute -inset-6 rounded-[32px] opacity-30 blur-[42px] pointer-events-none" aria-hidden="true" />

      <div
        ref={cardRef}
        className="relative bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[20px] overflow-hidden shadow-[0_2px_8px_rgba(15,23,42,.08),0_30px_60px_-15px_color-mix(in_srgb,var(--land-primary)_35%,transparent)] ring-1 ring-[color-mix(in_srgb,var(--land-primary)_12%,transparent)] transition-transform duration-[250ms] ease-out will-change-transform"
        style={{ transform: BASE_TRANSFORM }}
        aria-hidden="true"
      >
        {/* Cursor-follow gloss highlight - the classic "glass card catching light" cue that sells the tilt as a real 3D surface */}
        <div ref={glowRef} className="absolute inset-0 pointer-events-none z-10 mix-blend-overlay transition-[background] duration-150" />

        <div className="landing-aurora-deep px-4 py-3 flex items-center gap-2.5">
          <div className="flex gap-1.5">
            <div className="w-[11px] h-[11px] rounded-full bg-[#ff5f57]" />
            <div className="w-[11px] h-[11px] rounded-full bg-[#febc2e]" />
            <div className="w-[11px] h-[11px] rounded-full bg-[#28c840]" />
          </div>
          <p className="flex-1 text-center text-[13px] font-bold text-white/70">EasyFile2290</p>
        </div>

        <div className="p-4 bg-[var(--land-panel-2)]">
          <div className="grid grid-cols-3 gap-2 mb-2.5">
            {[
              { label: "Trucks", val: "3", cls: "text-[var(--land-primary)]" },
              { label: "Weight cat.", val: "V", cls: "text-[var(--land-emerald)]" },
              // Follows the calendar - was a fixed "Aug 31". Shows a dash once the
              // period's deadlines have all passed rather than an old date.
              { label: "Due", val: nextDue ? shortDueLabel(nextDue) : "—", cls: "text-[#d97706]" },
            ].map(({ label, val, cls }) => (
              <div key={label} className="bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[8px] px-3 py-2.5">
                <p className="text-[10px] font-bold text-[var(--land-ink)] uppercase tracking-[.05em] mb-1">{label}</p>
                <p className={`font-disp text-[18px] font-extrabold ${cls}`}>{val}</p>
              </div>
            ))}
          </div>

          <div className="bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[8px] p-3 mb-2">
            <div className="flex justify-between items-center mb-2">
              <p className="text-[11px] font-bold text-[var(--land-ink)]">TY2026 — Ray's Hauling LLC</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[color-mix(in_srgb,var(--land-emerald)_16%,transparent)] text-[var(--land-emerald)]">
                Ready to submit
              </span>
            </div>
            <div className="h-[5px] bg-[var(--land-line)] rounded-full overflow-hidden mb-1.5">
              <div className="h-full w-[95%] rounded-full landing-aurora" />
            </div>
            <p className="text-[10px] text-[var(--land-muted)] mb-2">VIN 1FUJGLDR9CLBP8834 · Category V · Logging rate</p>
            <div className="flex gap-1.5">
              <div className="px-3 py-1 rounded-[6px] bg-[var(--land-primary)] text-white text-[10px] font-bold">
                Submit to IRS
              </div>
              <div className="px-3 py-1 rounded-[6px] bg-[var(--land-panel-2)] text-[var(--land-ink)] border border-[var(--land-line)] text-[10px] font-bold">
                Review
              </div>
            </div>
          </div>

          <div className="bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[8px] p-3">
            <div className="flex justify-between items-center mb-2">
              <p className="text-[11px] font-bold text-[var(--land-ink)]">Schedule 1 · proof of payment</p>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[color-mix(in_srgb,var(--land-sky)_16%,transparent)] text-[var(--land-sky)]">
                Stamped
              </span>
            </div>
            <div className="flex gap-1">
              {["54%", "38%", "62%", "30%"].map((w, i) => (
                <div key={i} className="h-[6px] rounded-full bg-[var(--land-line)]" style={{ width: w }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div
        ref={(el) => (badgeRefs.current[0] = el)}
        className="absolute top-[-20px] right-[-20px] bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[12px] px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,.06),0_18px_32px_-10px_color-mix(in_srgb,var(--land-emerald)_40%,transparent)] flex items-center gap-2 whitespace-nowrap animate-float will-change-transform"
      >
        <CheckCircle2 size={18} className="text-[var(--land-emerald)]" />
        <div>
          <p className="text-xs font-semibold text-[var(--land-ink)]">Form 2290 e-filed</p>
          <p className="text-[11px] text-[var(--land-muted)]">TY2026 · Sent to the IRS</p>
        </div>
      </div>

      <div
        ref={(el) => (badgeRefs.current[1] = el)}
        className="absolute bottom-[-16px] left-[-22px] bg-[var(--land-panel)] border border-[var(--land-line)] rounded-[12px] px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,.06),0_18px_32px_-10px_color-mix(in_srgb,var(--land-primary)_35%,transparent)] flex items-center gap-2 whitespace-nowrap animate-float-slow will-change-transform"
      >
        <Truck size={18} className="text-[var(--land-primary)]" />
        <div>
          <p className="text-xs font-semibold text-[var(--land-ink)]">3 trucks added</p>
          <p className="text-[11px] text-[var(--land-muted)]">$658.00 balance due</p>
        </div>
      </div>
    </div>
  );
};

export default HeroMockup;
